import { Map } from 'components/map';
import StructureGridPolygonArea from 'components/map/structure-grid-polygon-area';
import { useCalculateMapDimensions } from 'components/map/utils';
import { useRef } from 'react';
import { Point } from 'types/api-responses';
import style from './epsg-projection-map.module.scss';
import { structureGridMapConfigAtom } from '../add-structure-grid/utils';

interface EpsgProjectionGeometry {
  min_x: number;
  max_x: number;
  min_y: number;
  max_y: number;
}

interface EpsgProjectionMapProps {
  geometry: EpsgProjectionGeometry;
  mapId: string;
  previewData: EpsgProjectionGeometry | undefined;
}

export const EpsgProjectionMap = ({
  geometry,
  mapId,
  previewData,
}: EpsgProjectionMapProps) => {
  const containerRef = useRef<null>(null);

  // Create polygon geometry for the EPSG selection
  const polygonGeometry = {
    type: 'Polygon' as const,
    coordinates: [
      [
        [geometry.min_x, geometry.min_y], // bottom-left
        [geometry.max_x, geometry.min_y], // bottom-right
        [geometry.max_x, geometry.max_y], // top-right
        [geometry.min_x, geometry.max_y], // top-left
        [geometry.min_x, geometry.min_y], // close the polygon
      ],
    ],
  };

  // Create polygon geometry for the preview data if available
  const previewGeometry = previewData
    ? {
        type: 'Polygon' as const,
        coordinates: [
          [
            [previewData.min_x, previewData.min_y], // bottom-left
            [previewData.max_x, previewData.min_y], // bottom-right
            [previewData.max_x, previewData.max_y], // top-right
            [previewData.min_x, previewData.max_y], // top-left
            [previewData.min_x, previewData.min_y], // close the polygon
          ],
        ],
      }
    : undefined;

  const { latitude, longitude, zoom } = useCalculateMapDimensions({
    points: [
      [geometry.min_x, geometry.max_y],
      [geometry.max_x, geometry.min_y],
    ] as [Point, Point],
    containerRef,
  });

  return (
    <div ref={containerRef}>
      <Map
        key={mapId}
        className={style.map}
        longitude={longitude}
        latitude={latitude}
        zoom={zoom - 1}
        mapConfigAtom={structureGridMapConfigAtom}
        showControls
        showMapFilters={false}
      >
        <StructureGridPolygonArea
          geometry={polygonGeometry}
          id={`${mapId}-epsg`}
        />
        {previewGeometry && (
          <StructureGridPolygonArea
            geometry={previewGeometry}
            id={`${mapId}-preview`}
            color="red"
          />
        )}
      </Map>
    </div>
  );
};
